import React from 'react';
import { 
  Box, 
  Typography, 
  Button,
  ButtonProps,
  SxProps,
  Theme,
} from '@mui/material';
import { spaces, palette } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';

interface PlanSettingsProps {
  title: string;
  description: string;
  button: {
    text: string;
    variant?: ButtonProps['variant'];
    color?: ButtonProps['color'];
    onClick: () => void;
    sx?: SxProps<Theme>;
  };
  Icon: React.ElementType;
  sxProps?: SxProps<Theme>;
}

const PlanSettings: React.FC<PlanSettingsProps> = (props) => {
  const { isXsScreen, isSmScreen, isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isMdScreen || isLgScreen;
  const { title, description, button, Icon, sxProps } = props;

  return (
    <Box
      data-testid={`plan-settings-${title}`} 
      sx={{
        width: isDesktopScreen ? '384px' : 'auto', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...sxProps,
      }}
    >  
      {!isXsScreen && (
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': {
              width: isLgScreen ? '320px' : '100%',
              height: isLgScreen ? '320px' : '100%',
            }
          }}
        >
          <Icon />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: isMdScreen ? '274px' : isLgScreen ? '180px' : 'auto',
          paddingX: spaces.lg,
          paddingBottom: spaces.lg,
          paddingTop: isXsScreen ? spaces.lg : 0,
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          alignSelf: 'stretch',
          gap: isXsScreen || isSmScreen ? spaces.sm : 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.xs,
            alignItems: 'flex-start',
            alignSelf: 'stretch',
          }}
        >
          <Typography 
            variant='h6' 
            color={palette.text.primary}
          >
            {title}
          </Typography>
          <Typography 
            variant='body2' 
            color={palette.text.secondary}
          >
            {description}
          </Typography>
        </Box>
        <Button
          fullWidth
          data-testid={`button-${title}`}
          variant={button.variant}
          color={button.color}
          size='large'
          onClick={button.onClick}
          sx={{ ...button?.sx }}
        >
          {button.text}
        </Button>
      </Box>
    </Box>
  );
};

export default PlanSettings;
